import { useState } from 'react';
import { Modal } from '@/components/common/AppModal';
import { Button, Form, NavLink } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import Select, { components } from 'react-select';
import {
  useCreateLandingUrlsMutation,
  useGetLandingUrlsForSelectQuery,
  useLeaveToLandingMutation,
  useMoveToLandingMutation
} from '@/app/KeywordTracker/api/keywordsApi';
import { Formik } from 'formik';
import { errorMessage, successMessage } from '@/utils/toast';
import FalconCloseButton from '@/components/common/FalconCloseButton';
import { useSelector } from 'react-redux';
import OptionLayoutLandingUrls from '@/app/common/components/OptionsLayout/OptionLayoutLandingUrls';
import Flex from '@/components/common/Flex';
import * as yup from 'yup';
import useDomain from '@/hooks/useDomain';
import { updateTransaction } from '@/app/common/components/ag-grid/updateTransaction';
import type { GridDrawerProps } from './types';
import type { NoticeProps } from 'react-select';
import type { SelectOption } from './types';

const MoveToLandingForSelectedRows = ({ show, setShow, gridRef }: GridDrawerProps) => {
  const handleClose = () => setShow(false);

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false}>
      <Modal.Header>
        <Modal.Title>
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <div className="m-0">مدیریت صفحه هدف</div>
          </div>
        </Modal.Title>
        <FalconCloseButton onClick={handleClose} />
      </Modal.Header>
      <MoveToLandingBody handleClose={handleClose} gridRef={gridRef} />
    </Modal>
  );
};

interface MoveToLandingBodyProps {
  handleClose: () => void;
  gridRef: GridDrawerProps['gridRef'];
}

const MoveToLandingBody = ({ handleClose, gridRef }: MoveToLandingBodyProps) => {
  const { domain_slug } = useDomain();

  const selectedKeys = useSelector(state => state.selectedKeys);
  const [moveToLanding] = useMoveToLandingMutation();
  const [leaveToLanding] = useLeaveToLandingMutation();
  const { data: landings, isFetching: isFetchingLandings } =
    useGetLandingUrlsForSelectQuery(domain_slug);
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <Modal.Body className="pb-0 pt-0">
      <Formik
        onSubmit={async ({ landing_url_id, isMove }, action) => {
          isMove
            ? moveToLanding({
                keyword_ids: selectedKeys.map(key => key.id),
                landing_url_id: landing_url_id.value,
                domain_slug
              })
                .unwrap()
                .then(res => {
                  updateTransaction(res.data, gridRef);
                  successMessage('صفحه هدف با موفقیت به کلمات اضافه شد');
                })
                .catch(err => {
                  errorMessage('خطایی در افزودن صفحه هدف وجود داره');
                  console.error(err);
                })
            : leaveToLanding({
                keyword_ids: selectedKeys.map(key => key.id),
                landing_url_id: landing_url_id.value,
                domain_slug
              })
                .unwrap()
                .then(res => {
                  updateTransaction(res.data, gridRef);
                  successMessage('صفحه هدف با موفقیت از کلمات حذف شد');
                })
                .catch(err => {
                  errorMessage('خطایی در حذف صفحه هدف از کلمات وجود داره');
                  console.error(err);
                });
          handleClose();
        }}
        initialValues={{
          isMove: null,
          inputValue: '',
          landing_url_id: null
        }}
        validationSchema={yup.object().shape({
          isMove: yup.boolean().required('عملیات رو مشخص کنید.'),
          landing_url_id: yup.object().required('صفحه هدف رو مشخص کنید.')
        })}
      >
        {({
          values,
          errors,
          touched,
          handleSubmit,
          setFieldError,
          setFieldValue
        }) => (
          <Form
            onSubmit={e => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <Form.Group className="my-3">
              <Form.Label>کلمات انتخاب شده ({selectedKeys.length})</Form.Label>
              <Form.Control
                // readOnly
                disabled
                rows={selectedKeys.length > 3 ? 3 : selectedKeys.length}
                as="textarea"
                value={selectedKeys.map(({ label }) => `${label}`).join('\n')}
              />
            </Form.Group>
            <Form.Group className="my-3">
              <Form.Label>انتخاب صفحه </Form.Label>
              {!isFetchingLandings && landings?.data && (
                <Select
                  classNamePrefix="react-select"
                  menuShouldScrollIntoView={true}
                  menuIsOpen={menuOpen}
                  onMenuOpen={() => setMenuOpen(true)}
                  // onMenuClose={() => setTimeout(() => setMenuOpen(false), 100)}
                  onInputChange={(newValue, { action }) => {
                    if (action !== 'input-blur' && action !== 'menu-close') {
                      setFieldValue('inputValue', newValue); // Only update input if not closing
                    } else {
                      setTimeout(() => setMenuOpen(false), 150);
                    }
                  }}
                  inputValue={values.inputValue}
                  placeholder="انتخاب کن"
                  openMenuOnFocus={true}
                  styles={{
                    menu: (baseStyles, state) => ({
                      position: 'absolute',
                      // overflow: 'auto',
                      zIndex: 9999,
                      width: '100%'
                    }),
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      direction: 'rtl !important',
                      borderColor:
                        errors.landing_url_id &&
                        touched.landing_url_id &&
                        'var(--falcon-form-invalid-border-color) !important',
                      paddingLeft: '10px',
                      paddingRight: '10px',
                      color: 'var(--falcon-emphasise) !important',
                      boxShadow: 'none !important',
                      borderRadius: '5px 5px 5px 5px !important',
                      width: '100% !important',
                      cursor: 'pointer'
                    }),
                    input: (baseStyles, state) => ({
                      ...baseStyles,
                      direction: 'ltr !important',
                      color: 'var(--falcon-emphasise) !important',
                      boxShadow: 'none !important'
                    })
                  }}
                  components={{
                    Option: OptionLayoutLandingUrls,
                    NoOptionsMessage: props => (
                      <NoOptionsMessage
                        {...props}
                        setFieldValue={setFieldValue}
                        setFieldError={setFieldError}
                      />
                    )
                  }}
                  onChange={e => {
                    setFieldValue('landing_url_id', e);
                  }}
                  value={values.landing_url_id}
                  isSearchable={true}
                  noOptionsMessage={() => 'یافت نشد'}
                  options={[
                    { label: 'صفحه پیدا شده در گوگل', value: null },
                    ...landings.data
                  ]}
                />
              )}
              <Form.Control.Feedback
                className="d-block"
                type={
                  errors.landing_url_id || errors.inputValue
                    ? 'invalid'
                    : 'valid'
                }
              >
                {(touched.landing_url_id || errors.inputValue) &&
                  (errors.landing_url_id || errors.inputValue)}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Label>عملیات</Form.Label>
            <Flex className="gap-3 flex-wrap">
              <Form.Check
                isInvalid={errors.isMove}
                type="radio"
                className="form-check radio-select"
              >
                <Form.Check.Input
                  isInvalid={errors.isMove}
                  onChange={({ target }) => {
                    target.checked && setFieldValue('isMove', true);
                  }}
                  checked={values.isMove}
                  type="radio"
                  id="add"
                />
                <Form.Check.Label htmlFor="add">
                  اضافه به صفحه هدف{' '}
                </Form.Check.Label>
              </Form.Check>{' '}
              <Form.Check type="radio" className="form-check radio-select">
                <Form.Check.Input
                  isInvalid={errors.isMove}
                  onChange={({ target }) => {
                    target.checked && setFieldValue('isMove', false);
                  }}
                  checked={values.isMove === false}
                  type="radio"
                  id="remove"
                />
                <Form.Check.Label htmlFor="remove">
                  خروج از صفحه هدف
                </Form.Check.Label>
              </Form.Check>
            </Flex>
            <Form.Control.Feedback
              className="d-block mt-n2 mb-2"
              type={errors.isMove ? 'invalid' : 'valid'}
            >
              {touched.isMove && errors.isMove}
            </Form.Control.Feedback>
            <Modal.Footer>
              <Button
                className="d-flex gap-2 align-items-center"
                variant="outline-secondary"
                onClick={handleClose}
              >
                <FontAwesomeIcon size="lg" icon="times" />
                لغو
              </Button>
              <Button
                className="d-flex gap-2 align-items-center border-0"
                // onClick={() => handleSubmit()}
                type="submit"
                variant="falcon-primary"
              >
                اعمال کن
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal.Body>
  );
};

export default MoveToLandingForSelectedRows;

interface NoOptionsMessageProps extends NoticeProps<SelectOption, false> {
  setFieldValue: (field: string, value: unknown) => void;
  setFieldError: (field: string, message: string | undefined) => void;
}

const NoOptionsMessage = (props: NoOptionsMessageProps) => {
  const { data: userInfo, isLoading, isError } = useGetUserInfoQuery();
  const { domain_slug, domain } = useDomain();
  const [createLandingUrls] = useCreateLandingUrlsMutation();
  const inputValue = props.selectProps.inputValue;
  const setInptut = value => props.setFieldValue('inputValue', value);
  return (
    <components.NoOptionsMessage {...props}>
      <Formik
        onSubmit={async ({ url }, action) => {
          createLandingUrls({
            url: url.trim(),
            keyword_ids: [],
            domain_slug
          })
            .unwrap()
            .then(res => {
              if (res?.success) {
                successMessage('صفحه هدف با موفقیت اضافه شد');
                setInptut('');
              } else if (res?.resultCode === 50004)
                errorMessage('صفحه هدف با این نشانی وجود داره');
            })
            .catch(err => {
              errorMessage('خطایی در ایجاد صفحه هدف وجود داره');
              console.error(err);
            });
          // setShow(false);
        }}
        initialValues={{
          url: inputValue || '',
          keyword_ids: []
        }}
        enableReinitialize
        validationSchema={yup.object().shape({
          url: yup
            .string()
            .trim()
            .required('لطفا نشانی صفحه هدف رو بنویسید.')
            .url('نشانی باید معتبر باشد.')
            .max(800, 'نشانی نباید بیشتر از ۲۰۰ کاراکتر باشد.')
        })}
      >
        {({ handleSubmit, errors }) => {
          if (errors.url) props.setFieldError('inputValue', errors.url);
          return (
            !isLoading && (
              <div
                className="d-flex  fs--1 align-items-center gap-2 justify-content-center"
                onMouseDown={e => e.stopPropagation()} // Prevent react-select focus
                onClick={e => e.stopPropagation()} // Prevent react-select focus
              >
                <div>یافت نشد!</div>
                <NavLink
                  className="d-flex border-bottom bordrer-1 navLinkForceColor active nav-link gap-1 justify-content-center align-items-center"
                  onClick={e => {
                    e.stopPropagation();
                    handleSubmit();
                  }}
                >
                  اضافه کن
                </NavLink>
              </div>
            )
          );
        }}
      </Formik>
    </components.NoOptionsMessage>
  );
};
